import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { TAGLINE } from "../i18n/translations";
import { BRAND } from "../config/brand";
import { LogoSlot } from "./LogoSlot";

const ease = [0.22, 1, 0.36, 1];

// Light sweep clipped to the logo's own shape, played once after the splash.
const LogoSheen = ({ src, play }) => {
  const mask = { WebkitMaskImage: `url(${src})`, maskImage: `url(${src})`, WebkitMaskSize: "contain", maskSize: "contain", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat", WebkitMaskPosition: "center", maskPosition: "center" };
  return (
    <motion.span
      aria-hidden="true"
      data-testid="hero-logo-sheen"
      className="pointer-events-none absolute inset-0 mix-blend-screen"
      style={{ ...mask, backgroundImage: "linear-gradient(105deg, transparent 38%, rgba(255,246,220,0.85) 50%, transparent 62%)", backgroundSize: "300% 100%", backgroundRepeat: "no-repeat" }}
      initial={{ backgroundPosition: "120% 0%", opacity: 0 }}
      animate={play ? { backgroundPosition: "-20% 0%", opacity: [0, 1, 1, 0] } : undefined}
      transition={{ delay: 1.1, duration: 1.8, ease: [0.45, 0, 0.25, 1], opacity: { delay: 1.1, duration: 1.8, times: [0, 0.15, 0.8, 1] } }}
    />
  );
};

export const Hero = ({ ready }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 110, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 110, damping: 16 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const haloY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  const show = ready ? "show" : "hidden";

  return (
    <section
      id="top"
      ref={ref}
      onPointerMove={onMove}
      onPointerLeave={reset}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden px-6 pb-16 pt-28"
    >
      <motion.div style={{ y: haloY }} className="pointer-events-none absolute inset-0">
        <div className="hero-grid absolute inset-0" />
      </motion.div>

      <motion.div style={{ y: contentY }} className="relative z-10 flex flex-col items-center text-center">
        <motion.p
          initial="hidden" animate={show}
          variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease } } }}
          className="relative z-10 mb-10 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-zinc-400"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-8 bg-gold/60" /> {t.eyebrow} <span className="h-px w-8 bg-gold/60" />
        </motion.p>

        <motion.div
          style={{ rotateX, rotateY, transformPerspective: 900 }}
          initial="hidden" animate={show}
          variants={{ hidden: { scale: 0.85, opacity: 0 }, show: { scale: 1, opacity: 1, transition: { duration: 1.1, ease } } }}
          className="relative"
        >
          <div className="pointer-events-none absolute -inset-28 rounded-full bg-[radial-gradient(circle,#000_0%,#000_42%,rgba(0,0,0,0.6)_58%,transparent_72%)] sm:-inset-36" data-testid="hero-logo-backdrop" />
          <div className="absolute -inset-6 animate-spin-slower rounded-full border border-gold/15 [border-top-color:rgba(212,175,55,0.6)]" />
          <div className="absolute -inset-12 rounded-full border border-white/[0.04]" />
          <span className="relative block">
            <LogoSlot src={BRAND.logo} alt="SYNrise" label="SYNrise logo" className="h-40 w-40 drop-shadow-[0_0_18px_rgba(249,115,22,0.22)] sm:h-52 sm:w-52" testId="hero-logo" />
            {BRAND.logo && <LogoSheen src={BRAND.logo} play={ready} />}
          </span>
        </motion.div>

        <h1 className="relative z-10 mt-14 overflow-hidden pb-2 font-display text-5xl font-extrabold leading-none tracking-[-0.04em] text-zinc-50 sm:text-6xl lg:text-7xl" data-testid="hero-title">
          <motion.span className="inline-block" initial="hidden" animate={show}
            variants={{ hidden: { y: "105%" }, show: { y: 0, transition: { delay: 0.15, duration: 1, ease } } }}>
            SYN<span className="font-serif text-[1.12em] font-normal italic tracking-normal text-gold">rise</span>
          </motion.span>
        </h1>

        <div className="relative z-10 mt-6 flex flex-col items-center sm:flex-row sm:gap-4" data-testid="hero-tagline">
          {TAGLINE.map((word, i) => (
            <span key={word} className="block overflow-hidden py-1">
              <motion.span
                className="block font-serif text-3xl italic text-zinc-200 sm:text-4xl"
                initial="hidden" animate={show}
                variants={{ hidden: { y: "110%" }, show: { y: 0, transition: { delay: 0.45 + i * 0.16, duration: 0.95, ease } } }}
              >
                {word}
              </motion.span>
            </span>
          ))}
        </div>
      </motion.div>

      <motion.a
        href="#collaboration"
        data-testid="hero-scroll-cue"
        initial={{ opacity: 0 }} animate={{ opacity: ready ? 1 : 0 }} transition={{ delay: 1.3, duration: 0.8 }}
        className="absolute bottom-[max(2rem,env(safe-area-inset-bottom))] z-10 flex flex-col items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400 hover:text-gold"
      >
        {t.scroll}
        <ArrowDown className="h-4 w-4 animate-bounce-soft" />
      </motion.a>
    </section>
  );
};
